import React, { useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { MdEdit, MdDelete, MdSettings } from "react-icons/md";
import Card from "components/card";

const StaffTable = ({
  data = [],
  loading = false,
  page = 1,
  limit = 10,
  total = 0,
  onPageChange = () => {},
  onLimitChange = () => {},
  onEdit = () => {},
  onPermissions = () => {},
  onDelete = () => {},
  canEdit = false,
  canDelete = false,
}) => {
  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: (info) => (
          <p className="text-sm font-medium text-navy-700 dark:text-white">
            {info.getValue()}
          </p>
        ),
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: (info) => (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {info.getValue()}
          </p>
        ),
      },
      {
        accessorKey: "phone",
        header: "Phone",
        cell: (info) => (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {info.getValue() || "—"}
          </p>
        ),
      },
      {
        accessorKey: "role",
        header: "Role",
        cell: (info) => (
          <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
            {info.getValue()}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) => {
          const status = info.getValue();
          const isActive = status === "active";
          return (
            <span
              className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                isActive
                  ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                  : "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400"
              }`}
            >
              {isActive ? "Active" : "Inactive"}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="flex items-center gap-2">
              {canEdit && (
                <button
                  onClick={() => onEdit(row.id)}
                  className="rounded-lg bg-blue-100 p-2 text-blue-600 transition-all hover:bg-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/40"
                  title="Edit staff member"
                >
                  <MdEdit className="h-4 w-4" />
                </button>
              )}
              {canEdit && (
                <button
                  onClick={() => onPermissions(row.id)}
                  className="rounded-lg bg-purple-100 p-2 text-purple-600 transition-all hover:bg-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:hover:bg-purple-900/40"
                  title="Manage permissions"
                >
                  <MdSettings className="h-4 w-4" />
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => onDelete(row.id, row.name)}
                  className="rounded-lg bg-red-100 p-2 text-red-600 transition-all hover:bg-red-200 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40"
                  title="Delete staff member"
                >
                  <MdDelete className="h-4 w-4" />
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [canEdit, canDelete, onEdit, onDelete, onPermissions]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalPages = Math.ceil(total / limit);

  return (
    <Card extra="overflow-x-auto">
      <div className="w-full">
        {/* Table */}
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-white/10">
              {table.getHeaderGroups().map((headerGroup) =>
                headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-6 py-4 text-left text-sm font-bold text-navy-700 dark:text-white"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Loading staff...
                    </span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No staff members found
                  </p>
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-200 hover:bg-gray-50 dark:border-white/10 dark:hover:bg-navy-700/50"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-6 py-4">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 0 && !loading && (
        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 dark:border-white/10">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Limit selector */}
            <select
              value={limit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-700 dark:text-white"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>

            {/* Pagination buttons */}
            <button
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:bg-navy-700 dark:text-gray-400 dark:hover:bg-navy-600"
            >
              Previous
            </button>

            {/* Page info */}
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {page} of {totalPages}
            </span>

            <button
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:bg-navy-700 dark:text-gray-400 dark:hover:bg-navy-600"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default StaffTable;
